import { useEffect, useState } from "react";
import { $trimTextContentFromAnchor } from "@lexical/selection";
import { $restoreEditorState } from "@lexical/utils";
import {
  $getRoot,
  $getSelection,
  $isRangeSelection,
  EditorState,
  LexicalEditor,
  RootNode,
} from "lexical";

export function useMaxLength(
  maxLength?: number,
  editor?: React.RefObject<LexicalEditor>
) {
  const [length, setLength] = useState(0);

  function refreshCount() {
    editor?.current?.getEditorState().read(() => {
      const rootNode = $getRoot();
      setLength(rootNode.getTextContentSize());
    });
  }

  useEffect(() => {
    refreshCount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!maxLength || !editor?.current) return;

    let lastRestoredEditorState: EditorState | null = null;

    return editor.current.registerNodeTransform(
      RootNode,
      (rootNode: RootNode) => {
        const selection = $getSelection();
        if (!$isRangeSelection(selection) || !selection.isCollapsed()) {
          return;
        }
        const prevEditorState = editor.current!.getEditorState();
        const prevTextContentSize = prevEditorState?.read(() =>
          rootNode.getTextContentSize()
        );
        const textContentSize = rootNode.getTextContentSize();
        if (prevTextContentSize !== textContentSize) {
          const delCount = textContentSize - maxLength;
          const anchor = selection.anchor;

          if (delCount > 0) {
            // Restore the old editor state instead if the last
            // text content was already at the limit.
            if (
              prevTextContentSize <= maxLength &&
              lastRestoredEditorState !== prevEditorState
            ) {
              lastRestoredEditorState = prevEditorState;
              $restoreEditorState(editor.current!, prevEditorState);
            } else {
              $trimTextContentFromAnchor(editor.current!, anchor, delCount);
            }
          } else {
            setLength(textContentSize);
          }
        }
      }
    );
  }, [editor, maxLength]);

  return {
    length,
    refreshCount,
  };
}
